/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import DatePickerCustom from '../../DatePickerCustom/DatePickerCustom';
import styles from './MultiPOGeneralDetails.module.scss';
import { CheckPicker, CheckTreePicker, SelectPicker } from 'rsuite';
import { useMultiplePo } from './contexts/MultiplePoContext';
import { useAuth } from '@/Components/Auth/Login/auth';
import { RefreshCcw, Upload, Plus, ClipboardPaste } from 'lucide-react';

const iconMap = {
	upload: Upload,
	plus: Plus,
	refresh: RefreshCcw,
	paste: ClipboardPaste,
};

function MultiPOGeneralDetails(props) {
	const {
		fields = [],
		externalStyles = {},
		editMode = false,
		viewMode = false,
		lists = {},
		onFieldChange = () => { },
		layoutConfig = {},
		modeName = '',
	} = props;
	const { state } = useMultiplePo();
	const { appConfig = {}, userConfig = {} } = useAuth();
	const { configs = {} } = appConfig || {};
	const customerId = appConfig?.customerId;


	// Auto-set shift value when shiftList loads (only for longTermProduction mode)
	useEffect(() => {
		if (modeName === 'longTermProduction' && lists?.shiftList?.length > 0 && !state?.shift) {
			onFieldChange('shift', lists.shiftList[0]?.value || lists.shiftList[0]?.id);
		}
	}, [modeName, lists?.shiftList, state?.shift, onFieldChange]);

	const defaultClasses = {
		containerClass: `${styles.root} relative ${editMode ? 'opacity-70' : ''}`,
		fieldWrapperClass: 'flex flex-col gap-1.5 flex-1 min-w-[200px] max-w-[280px] items-start',
		labelClass: 'text-[13px] font-semibold text-gray-700 leading-[1.2] text-left w-full m-0 p-0',
		inputClass: 'w-full',
		tabContainerClass: 'flex gap-1 w-full',
		selectedTabClass: 'flex-1 px-3 py-1.5 text-[12px] font-semibold border rounded min-h-[36px] h-auto bg-amber-300 text-black border-amber-300',
		unselectedTabClass: 'flex-1 px-3 py-1.5 text-[12px] font-medium border rounded min-h-[36px] h-auto bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700',
	};

	const containerClass = layoutConfig.containerClass || defaultClasses.containerClass;
	const fieldWrapperClass = layoutConfig.fieldWrapperClass || defaultClasses.fieldWrapperClass;
	const labelClass = layoutConfig.labelClass || defaultClasses.labelClass;
	const inputClass = layoutConfig.inputClass || defaultClasses.inputClass;
	const tabContainerClass = layoutConfig.tabContainerClass || defaultClasses.tabContainerClass;
	const selectedTabClass = layoutConfig.selectedTabClass || defaultClasses.selectedTabClass;
	const unselectedTabClass = layoutConfig.unselectedTabClass || defaultClasses.unselectedTabClass;

	const filterShifts = (ele) => {
		if (moment(new Date()).isSame(moment(), 'day') && moment(ele?.endDate).isSameOrAfter()) {
			const startDate = moment(ele?.startDate, 'YYYY-MM-DD');
			const endDate = moment(ele?.endDate, 'YYYY-MM-DD');
			const currentTime = moment().startOf('day');
			if (ele?.process?.[0]?.endTime === '12:00 AM') return true;
			return currentTime.isSameOrAfter(startDate) && currentTime.isSameOrBefore(endDate);
		}
		return true;
	};

	const disabledYesterday = (rsuiteDate) => {
		if (modeName === 'orderWise') {
			const tomorrowAtMidnight = moment().add(1, 'days').startOf('day');
			return rsuiteDate < tomorrowAtMidnight;
		}
		const currentDateAtMidnight = moment().startOf('day');
		return rsuiteDate < currentDateAtMidnight;
	};
	const shouldDisplayField = (field, state) => {
		if (field?.hideInEditMode && editMode) {
			return false;
		}
		if (field?.showOnlyInEditMode && !editMode) {
			return false;
		}
		if (!field?.dependsOn || field?.dependsOn?.length === 0) {
			return true;
		}
		return field?.dependsOn?.some(condition =>
			Object?.entries(condition)?.every(
				([key, value]) =>
					String(state?.[key])?.toLowerCase() === String(value)?.toLowerCase()
			)
		);
	};

	const getExtraInfo = (field) => {
		if (!field.extraInfo) return null;
		const selectedValue = state[field.id];
		if (!selectedValue) return null;

		const list = lists[field.dataKey] || [];
		const selectedObj =
			typeof selectedValue === 'object'
				? selectedValue
				: list.find(ele => ele.id === selectedValue);

		if (!selectedObj) return null;

		if (field.extraInfo === 'SHIFT_TIME') {
			const start =
				selectedObj.startTime || selectedObj?.process?.[0]?.startTime;
			const end =
				selectedObj.endTime || selectedObj?.process?.[0]?.endTime;

			if (!start || !end) return null;

			return `${start} - ${end}`;
		}

		return null;
	};

	const renderFields = (field) => {
		if (!shouldDisplayField(field, state)) return null;
		// Disable all fields after step 1 for longTermProduction mode
		const isStepDisabled = modeName === 'longTermProduction' && state?.currentStep > 1 && !field?.alwaysEnabled;
		const isGlobalDisabled = (editMode || viewMode) && !field?.alwaysEnabled;
		const isDisabled = isGlobalDisabled || isStepDisabled || (field?.enableField?.length > 0 ? !field?.enableField?.every((ele) => state[ele]) : field?.disabled ?? false);
		const cleanable = field?.cleanable ?? true;
		let selectedList = lists[field?.dataKey];
		if (field?.noExpired) {
			selectedList = selectedList?.filter(filterShifts)
		}

		switch (field.type) {
			case 'select':
				return (
					<div key={field?.id} className={`${fieldWrapperClass} ${field?.wrapperClass || ''}`}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field?.label} {field?.required && <span className="ml-0.5">*</span>}
						</label>
						<SelectPicker
							data={field?.list || lists?.[field?.dataKey]}
							value={state?.[field?.id]?.id || state?.[field?.id]}
							onChange={(value) => onFieldChange(field?.id, value)}
							disabled={isDisabled}
							placeholder={field?.placeholder || `Select ${field?.label}`}
							groupBy={field?.groupBy || undefined}
							className={`${field?.inputClass || inputClass} [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded [&_.rs-picker-toggle]:px-2 [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] disabled:[&_.rs-picker-toggle]:bg-gray-100 disabled:[&_.rs-picker-toggle]:cursor-not-allowed disabled:[&_.rs-picker-toggle]:opacity-60 ${!layoutConfig.containerClass ? '[&_.rs-picker-toggle]:h-10 [&_.rs-picker-toggle]:py-2 [&_.rs-picker-toggle]:text-[14px]' : '[&_.rs-picker-toggle]:h-8 [&_.rs-picker-toggle]:py-1 [&_.rs-picker-toggle]:text-[13px]'}`}
							menuStyle={{
								zIndex: 4000,
							}}
							cleanable={cleanable}
						/>
						{!layoutConfig.containerClass && field?.extraInfo && (
							<div className="text-[11px] text-gray-500 mt-[3px] px-1.5 py-[3px] bg-gray-100 rounded text-center leading-[1.2] w-full">
								{getExtraInfo(field)}
							</div>
						)}
					</div>
				);
			case 'multiSelect':
				return (
					<div key={field.id} className={fieldWrapperClass}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field.label} {field.required && <span className="text-red-500 ml-0.5">*</span>}
						</label>
						<CheckPicker
							data={field?.list || lists?.[field?.dataKey]}
							value={state?.[field?.id]?.id || state?.[field?.id]}
							onChange={(value) => onFieldChange(field?.id, value)}
							disabled={isDisabled}
							placeholder={field?.placeholder || `Select ${field?.label}`}
							groupBy={field?.groupBy || undefined}
							className={`${field?.inputClass || inputClass} [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded [&_.rs-picker-toggle]:px-2 [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] disabled:[&_.rs-picker-toggle]:bg-gray-100 disabled:[&_.rs-picker-toggle]:cursor-not-allowed disabled:[&_.rs-picker-toggle]:opacity-60 ${!layoutConfig.containerClass ? '[&_.rs-picker-toggle]:h-10 [&_.rs-picker-toggle]:py-2 [&_.rs-picker-toggle]:text-[14px]' : '[&_.rs-picker-toggle]:h-8 [&_.rs-picker-toggle]:py-1 [&_.rs-picker-toggle]:text-[13px]'}`}
							menuStyle={{
								zIndex: 4000,
							}}
							cleanable={cleanable}
						/>
						{!layoutConfig.containerClass && field?.extraInfo && (
							<div className="text-[11px] text-gray-500 mt-[3px] px-1.5 py-[3px] bg-gray-100 rounded text-center leading-[1.2] w-full">
								{getExtraInfo(field)}
							</div>
						)}
					</div>
				);
			case 'checkTreePicker':
				return (
					<div key={field.id} className={fieldWrapperClass}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field.label} {field.required && <span className="text-red-500 ml-0.5">*</span>}
						</label>
						<CheckTreePicker
							data={field?.list || lists?.[field?.dataKey]}
							value={state?.[field?.id]?.id || state?.[field?.id]}
							onChange={(value) => onFieldChange(field?.id, value)}
							disabled={isDisabled}
							placeholder={field?.placeholder || `Select ${field?.label}`}
							groupBy={field?.groupBy || undefined}
							className={`${field?.inputClass || inputClass} [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded [&_.rs-picker-toggle]:px-2 [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] disabled:[&_.rs-picker-toggle]:bg-gray-100 disabled:[&_.rs-picker-toggle]:cursor-not-allowed disabled:[&_.rs-picker-toggle]:opacity-60 ${!layoutConfig.containerClass ? '[&_.rs-picker-toggle]:h-10 [&_.rs-picker-toggle]:py-2 [&_.rs-picker-toggle]:text-[14px]' : '[&_.rs-picker-toggle]:h-8 [&_.rs-picker-toggle]:py-1 [&_.rs-picker-toggle]:text-[13px]'}`}
							menuStyle={{
								zIndex: 4000,
							}}
							cleanable={cleanable}
						/>
						{!layoutConfig.containerClass && field?.extraInfo && (
							<div className="text-[11px] text-gray-500 mt-[3px] px-1.5 py-[3px] bg-gray-100 rounded text-center leading-[1.2] w-full">
								{getExtraInfo(field)}
							</div>
						)}
					</div>
				);
			case 'date':
				return (
					<div key={field.id} className={fieldWrapperClass}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field.label} {field.required && <span className="text-red-500 ml-0.5">*</span>}
						</label>
						<DatePickerCustom
							selectedDate={state?.[field?.id]}
							selectDate={(date) => onFieldChange(field?.id, date)}
							style={{
								minWidth: layoutConfig.inputClass ? '120px' : '100%',
							}}
							disabled={isDisabled}
							oneTap
							defaultValue={state?.[field?.id]}
							shouldDisableDate={field?.shouldDisableDate ? disabledYesterday : false}
							className={`${inputClass} [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded-lg [&_.rs-picker-toggle]:px-3 [&_.rs-picker-toggle]:py-2 [&_.rs-picker-toggle]:text-[14px] [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:h-10 [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] ${editMode ? 'pointer-events-none select-none' : ''}`}
						/>
					</div>
				);

			case 'tabs':
				const tabOptions = field?.tabOptions || configs?.[field?.configOptions] || ['PO', 'CMS', 'CMS_INDENT'];
				return (
					<div key={field.id} className={`${fieldWrapperClass} !max-w-none w-auto min-w-fit !flex-none`}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field.label} {field.required && <span className="text-red-500 ml-0.5">*</span>}
						</label>
						<div className={`${tabContainerClass} flex-wrap`}>
							{tabOptions?.map((type) => {
								const getTabLabel = (val) => {
									if (val?.toUpperCase() === 'PO') return 'Secondary Packaging Dispatch / PO';
									if (val?.toUpperCase() === 'CMS') return 'Primary Packaging Dispatch / CMS';
									return val;
								};
								return (
									<button
										key={type}
										type="button"
										onClick={() => onFieldChange(field?.id, type)}
										disabled={isDisabled}
										className={`${state?.[field?.id] === type ? selectedTabClass : unselectedTabClass} transition-all disabled:opacity-60 disabled:cursor-not-allowed`}
									>
										{getTabLabel(type)}
									</button>
								);
							})}
						</div>
					</div>
				);
			case 'text':
				return (
					<div key={field.id} className={`${fieldWrapperClass} ${field?.wrapperClass || ''}`}>
						<label className={`${labelClass} ${field?.required ? 'text-red-500' : ''}`}>
							{field.label} {field.required && <span className="ml-0.5">*</span>}
						</label>
						<input
							type="text"
							value={state[field?.id] || ''}
							onChange={(e) => onFieldChange(field?.id, e.target.value)}
							disabled={isDisabled}
							placeholder={field.placeholder || field.label}
							className={`${field?.inputClass || inputClass} border border-gray-300 rounded px-2 leading-[1.2] bg-white text-gray-700 transition-all hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 placeholder:text-gray-400 ${!layoutConfig.containerClass ? 'py-2 text-[14px] h-10' : 'py-1 text-[13px] h-8'}`}
						/>
					</div>
				);
			case 'display':
				return (
					<div key={field.id} className="flex flex-col gap-1.5 flex-1 flex-shrink-0 items-center">
						<label className={`text-[13px] font-semibold leading-[1.2] text-left w-full m-0 p-0 ${field?.required ? 'text-red-500' : 'text-gray-700'}`}>
							{field.label} {field.required && <span className="ml-0.5">*</span>}
						</label>
						<span className="w-full border-b border-gray-300 rounded px-2.5 py-1.5 text-[13px] h-9  bg-white text-gray-700 transition-all hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 placeholder:text-gray-400"
						>
							{state?.[field?.id]}
						</span>
					</div>
				);

			case 'button':
				const Icon = field.buttonIcon;
				return (
					<div key={field.id} className={`${fieldWrapperClass} ${field?.wrapperClass || ''}`}>
						<button
							type="button"
							onClick={() => onFieldChange(field?.id, 'click')}
							disabled={isDisabled}
							className={`${field.buttonClass || 'bg-teal-600 hover:bg-teal-700 text-white'} px-4 py-1.5 rounded-md text-[13px] font-semibold h-8 flex items-center gap-2.5 whitespace-nowrap shadow-sm active:scale-[0.98] transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:shadow-none disabled:cursor-not-allowed`}
						>
							{field.buttonIcon && iconMap[field.buttonIcon] && (() => {
								const IconComp = iconMap[field.buttonIcon];
								return <IconComp size={16} />;
							})()}
							{field.buttonText}
						</button>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div
			className={containerClass}
			style={{ position: 'relative', ...(externalStyles && externalStyles) }}
		>
			{fields?.map(renderFields)}
		</div>
	);
}

export default MultiPOGeneralDetails; 